import { useEffect, useState } from "react";
import { ptBR } from "react-day-picker/locale";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDisplayDate, parseIsoDate, todayIsoDate, toIsoDate } from "@/lib/dateInput";
import type { MutationCallbacks } from "@/hooks/useMarkInstallmentAsPaid";

interface MarkInstallmentAsPaidPopoverProps {
  installmentId: string;
  onConfirm: (paidAtDate: string, callbacks?: MutationCallbacks) => void;
  loading?: boolean;
  error?: string | null;
  disabled?: boolean;
}

function stopPropagation(event: React.MouseEvent | React.KeyboardEvent) {
  event.stopPropagation();
}

export default function MarkInstallmentAsPaidPopover({
  installmentId,
  onConfirm,
  loading = false,
  error,
  disabled = false,
}: MarkInstallmentAsPaidPopoverProps) {
  const [open, setOpen] = useState(false);
  const [paidAtDate, setPaidAtDate] = useState(todayIsoDate);
  const selectedPaidAt = parseIsoDate(paidAtDate);
  const [month, setMonth] = useState<Date>(() => selectedPaidAt ?? new Date());

  useEffect(() => {
    if (selectedPaidAt) {
      setMonth(selectedPaidAt);
    }
  }, [paidAtDate]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      const today = todayIsoDate();
      setPaidAtDate(today);
      setMonth(parseIsoDate(today) ?? new Date());
    }
  };

  const handleConfirm = () => {
    onConfirm(paidAtDate, {
      onCompleted: () => {
        setOpen(false);
        setPaidAtDate(todayIsoDate());
      },
    });
  };

  const handleCancel = () => {
    setOpen(false);
    setPaidAtDate(todayIsoDate());
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled || loading}
          className="h-auto p-0 text-xs font-light uppercase tracking-widest text-primary-700 hover:text-primary-950"
          onClick={stopPropagation}
        >
          Marcar como pago
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto rounded-none border-primary-200 p-0 dark:border-primary-800"
        align="end"
        side="top"
        sideOffset={8}
        collisionPadding={16}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onClick={stopPropagation}
        onKeyDown={stopPropagation}
      >
        <div className="border-b border-primary-100 px-4 py-3 dark:border-primary-800">
          <p className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
            Marcar como pago
          </p>
          <p
            id={`paid-at-label-${installmentId}`}
            className="mt-2 text-xs uppercase tracking-widest text-primary-500 dark:text-primary-500"
          >
            Data do pagamento
          </p>
          <p className="mt-1 text-sm font-light tabular-nums text-primary-950 dark:text-white">
            {formatDisplayDate(paidAtDate)}
          </p>
        </div>

        <Calendar
          mode="single"
          selected={selectedPaidAt}
          month={month}
          onMonthChange={setMonth}
          onSelect={(date) => {
            if (date) setPaidAtDate(toIsoDate(date));
          }}
          locale={ptBR}
          disabled={loading}
        />

        <div className="space-y-2 border-t border-primary-100 px-4 py-3 dark:border-primary-800">
          {error && (
            <p className="text-xs text-red-600" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={loading}
              className="text-xs uppercase tracking-widest"
              onClick={handleCancel}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={loading}
              className="text-xs uppercase tracking-widest"
              onClick={handleConfirm}
            >
              {loading ? "Marcando..." : "Confirmar"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
